import React, { useEffect, useState } from 'react'

import {
  TextInput,
  required,
  SelectInput,
  minLength,
  BooleanInput,
} from 'react-admin'

import { makeStyles } from '@material-ui/core/styles'
import { Typography, Grid, Paper } from '@material-ui/core'

import { ColorPicker, ResourceSelector, Tooltip } from 'components'
import { slugValidate, numberValidate } from 'utils/formValidations'

import { Props } from './types'

const useStyles = makeStyles({
  gridContainer: {
    width: '100%',
    margin: 0,
    padding: 0,
  },
  paperContainer: {
    padding: 16,
  },
})

export const Form: React.FC<Props> = ({ formData, title }) => {
  const classes = useStyles()
  const [images, setImages] = useState(Array(3))
  const [category, setCategory] = useState('Texto do botão')
  const [enableSecondaryButton, setEnableSecondaryButton] = useState(
    !!formData.stylesheet_text.button2?.slug,
  )

  const handleSelect = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCategory(event.target.value)
  }
  const toggleSecondaryButton = (checked: boolean) => {
    setEnableSecondaryButton(checked)

    if (!checked) {
      formData.stylesheet_text.button2 = null
      formData.stylesheet.button2 = null
    }
  }

  useEffect(() => {
    if (formData && formData?.id) {
      setImages(formData.images)
    }
  }, [])

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={2}
      className={classes.gridContainer}
    >
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Paper className={classes.paperContainer}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <hr />
          </Grid>

          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem"
                    source="order"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.text_direction'}>
                  <SelectInput
                    fullWidth
                    label="Posição do texto"
                    source="text_direction"
                    choices={[
                      { id: 'left', name: 'Esquerda' },
                      { id: 'right', name: 'Direita' },
                    ]}
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Formato do Slug: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Letras minúsculas separadas por hifen, sem espaços, acentos ou caracteres especiais.`}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  {`[nome do autor]-[nome do curso ou série]`}
                </Typography>

                <Tooltip title={'slider.slug'}>
                  <TextInput
                    fullWidth
                    label="Slug"
                    source="slug"
                    validate={[required('Obrigatório'), slugValidate]}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <SelectInput
                label="Visibilidade"
                source="active"
                choices={[
                  { id: true, name: 'Visível' },
                  {
                    id: false,
                    name: 'Oculto',
                  },
                ]}
                validate={[required('Obrigatório')]}
                externalLoading={false}
                onChange={(event) => handleSelect(event)}
              />
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Imagem de Fundo: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Banner – Desktop
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'slider.image_desktop.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[0].order"
                    validate={[
                      required('Obrigatório'),
                      numberValidate,
                      minLength(2, 'Mínimo 2 caracteres numéricos'),
                    ]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.image_desktop.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[0].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Imagem horizontal: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[0].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[0]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'banner',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Banner – Mobile
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'slider.image_mobile.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[2].order"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.image_mobile.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[2].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Imagem Vertical: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[2].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[2]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'banner-mobile',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Marcador: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <Tooltip title={'slider.mark.type'}>
                  <SelectInput
                    fullWidth
                    label="Tipo"
                    source="stylesheet.mark.type"
                    choices={[
                      { id: 'contained', name: 'Opaco - contained' },
                      { id: 'outlined', name: 'Borda - outlined' },
                    ]}
                    validate={[required('Obrigatório')]}
                    externalLoading={false}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={5} xl={5}>
                <Tooltip title={'slider.mark.text'}>
                  <TextInput
                    fullWidth
                    label="Texto"
                    source="stylesheet_text.mark.text"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid container spacing={2} className={classes.gridContainer}>
                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor do texto"
                    source="stylesheet.mark.text_color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor da Borda"
                    source="stylesheet.mark.color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Conteúdo: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Logo do Banner
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'slider.logo.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[1].order"
                    validate={[
                      required('Obrigatório'),
                      numberValidate,
                      minLength(2, 'Mínimo 2 caracteres numéricos'),
                    ]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.logo.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[1].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[1].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[1]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'logo',
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.title'}>
                  <TextInput fullWidth label="Título" source="title" />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor do título"
                  source="stylesheet.text_color.title"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.subtitle'}>
                  <TextInput fullWidth label="Subtítulo" source="subtitle" />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor do subtítulo"
                  source="stylesheet.text_color.subtitle"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'slider.description'}>
                  <TextInput
                    fullWidth
                    label="Description"
                    source="description"
                    validate={[required('Obrigatório')]}
                    multiline
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor da descrição"
                  source="stylesheet.text_color.description"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Botão Primário: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <Tooltip title={'slider.button.type'}>
                  <SelectInput
                    fullWidth
                    label="Tipo"
                    source="stylesheet.button.type"
                    choices={[
                      { id: 'contained', name: 'Opaco - contained' },
                      { id: 'outlined', name: 'Borda - outlined' },
                    ]}
                    validate={[required('Obrigatório')]}
                    externalLoading={false}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                <Tooltip title={'slider.button.text'}>
                  <TextInput
                    fullWidth
                    label="Texto"
                    source="stylesheet_text.button.text"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid
                container
                spacing={2}
                className={classes.gridContainer}
                direction="row"
              >
                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor de fundo"
                    source="stylesheet.button.color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor do texto"
                    source="stylesheet.button.text_color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={12} xl={12}>
                <Tooltip title={'slider.button.slug'}>
                  <TextInput
                    fullWidth
                    label="Link URL"
                    source="stylesheet_text.button.slug"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Botão Secundário: `}</Typography>
                <BooleanInput
                  label="Habilitar"
                  source="button2.enabled"
                  defaultValue={enableSecondaryButton}
                  onChange={(e) => toggleSecondaryButton(e)}
                />
              </Grid>
              {enableSecondaryButton && (
                <>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <Tooltip title={'slider.button2.type'}>
                      <SelectInput
                        fullWidth
                        label="Tipo"
                        source="stylesheet.button2.type"
                        choices={[
                          // { id: 'contained', name: 'Opaco - contained' },
                          { id: 'outlined', name: 'Borda - outlined' },
                        ]}
                        externalLoading={false}
                        validate={
                          enableSecondaryButton
                            ? [required('Obrigatório')]
                            : null
                        }
                      />
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <Tooltip title={'slider.button2.category'}>
                      <SelectInput
                        fullWidth
                        label="Categoria"
                        source="stylesheet_text.button2.category"
                        choices={[
                          {
                            id: 'live-class',
                            name: 'Live Class (Salvar na agenda)',
                          },
                        ]}
                        externalLoading={false}
                        onChange={(event) => handleSelect(event)}
                        validate={
                          enableSecondaryButton
                            ? [required('Obrigatório')]
                            : null
                        }
                      />
                    </Tooltip>
                  </Grid>

                  <Grid
                    container
                    spacing={2}
                    className={classes.gridContainer}
                    direction="row"
                  >
                    <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                      <ColorPicker
                        fullWidth
                        label="Cor de fundo"
                        source="stylesheet.button2.color"
                        readOnly
                        validate={
                          enableSecondaryButton
                            ? [required('Obrigatório')]
                            : null
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                      <ColorPicker
                        fullWidth
                        label="Cor do texto"
                        source="stylesheet.button2.text_color"
                        readOnly
                        validate={
                          enableSecondaryButton
                            ? [required('Obrigatório')]
                            : null
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6} lg={12} xl={12}>
                    <Tooltip title={'stylesheet.button2.slug'}>
                      <TextInput
                        fullWidth
                        label={
                          category === 'class'
                            ? 'Slug da aula'
                            : category === 'live-class'
                            ? 'Link para agenda'
                            : 'Escolha uma categoria'
                        }
                        source="stylesheet_text.button2.slug"
                        validate={
                          enableSecondaryButton
                            ? [required('Obrigatório')]
                            : undefined
                        }
                      />
                    </Tooltip>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}
